import React, { useState, useMemo } from "react";
import {
  Card as UICard,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/Base/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/Base/Table";
import { Button } from "@/components/Base/Button";
import { Input } from "@/components/Base/Input";
import { Badge } from "@/components/Base/Badge";
import { Skeleton } from "@/components/Base/Skeleton";
import {
  TableIcon,
  SearchIcon,
  SortAscIcon,
  SortDescIcon,
  FilterIcon,
} from "lucide-react";
import {
  ContainerTimeMetric,
  SerializedContainerTimeMetric,
} from "@/types/v2/PrecomputedMetrics";
import { firestoreToDateTime, safeFirestoreToDateTime } from "@/lib/utils";

interface MetricsTableViewProps {
  metric:
    | ContainerTimeMetric
    | SerializedContainerTimeMetric
    | null
    | undefined;
  isLoading: boolean;
  timeUnit: "minutes" | "hours" | "days";
}

type SortField =
  | "cardNumber"
  | "customerName"
  | "timeDifference"
  | "employeeName";
type SortDirection = "asc" | "desc";

export const MetricsTableView: React.FC<MetricsTableViewProps> = ({
  metric,
  isLoading,
  timeUnit,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<SortField>("timeDifference");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const filteredAndSortedData = useMemo(() => {
    if (!metric?.calculations) return [];

    let filtered = metric.calculations;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered
        .filter(
          (calc) =>
            calc.cardNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            calc.customerName
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            calc.technicianName
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            calc.serviceAdvisorName
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
        )
        .map((i) => ({
          ...i,
          firstEventTime:
            safeFirestoreToDateTime(i.firstEventTime) ?? new Date(),
          secondEventTime:
            safeFirestoreToDateTime(i.secondEventTime) ?? new Date(),
        }));
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case "cardNumber":
          aValue = a.cardNumber || "";
          bValue = b.cardNumber || "";
          break;
        case "customerName":
          aValue = a.customerName || "";
          bValue = b.customerName || "";
          break;
        case "timeDifference":
          aValue = a.timeDifference;
          bValue = b.timeDifference;
          break;
        case "employeeName":
          aValue = a.technicianName || a.serviceAdvisorName || "";
          bValue = b.technicianName || b.serviceAdvisorName || "";
          break;
        default:
          aValue = 0;
          bValue = 0;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortDirection === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortDirection === "asc"
          ? (aValue as number) - (bValue as number)
          : (bValue as number) - (aValue as number);
      }
    });

    return filtered;
  }, [metric?.calculations, searchTerm, sortField, sortDirection]);

  const formatValue = (value: number) => {
    return value.toFixed(1);
  };

  const getUnitLabel = () => {
    switch (timeUnit) {
      case "minutes":
        return "min";
      case "hours":
        return "hrs";
      case "days":
        return "days";
      default:
        return "";
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <SortAscIcon className="h-4 w-4" />
    ) : (
      <SortDescIcon className="h-4 w-4" />
    );
  };

  if (isLoading) {
    return (
      <UICard>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TableIcon className="h-5 w-5" />
            Detailed Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </div>
        </CardContent>
      </UICard>
    );
  }

  if (!metric || !metric.calculations || metric.calculations.length === 0) {
    return (
      <UICard>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TableIcon className="h-5 w-5" />
            Detailed Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-8 text-center text-muted-foreground">
            No detailed data available
          </div>
        </CardContent>
      </UICard>
    );
  }

  return (
    <UICard>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TableIcon className="h-5 w-5" />
          Detailed Data
          <Badge variant="secondary" className="ml-auto">
            {filteredAndSortedData.length} records
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Search and Filter Controls */}
          <div className="flex items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
              <Input
                placeholder="Search cards, customers, employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" size="sm">
              <FilterIcon className="mr-2 h-4 w-4" />
              Filter
            </Button>
          </div>

          {/* Data Table */}
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort("cardNumber")}
                  >
                    <div className="flex items-center gap-1">
                      Card Number
                      <SortIcon field="cardNumber" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort("customerName")}
                  >
                    <div className="flex items-center gap-1">
                      Customer
                      <SortIcon field="customerName" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-right hover:bg-gray-50"
                    onClick={() => handleSort("timeDifference")}
                  >
                    <div className="flex items-center justify-end gap-1">
                      Time ({getUnitLabel()})
                      <SortIcon field="timeDifference" />
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort("employeeName")}
                  >
                    <div className="flex items-center gap-1">
                      Employee
                      <SortIcon field="employeeName" />
                    </div>
                  </TableHead>
                  <TableHead>Events</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSortedData.map((calculation) => {
                  const firstEventDate = firestoreToDateTime(
                    calculation.firstEventTime
                  );
                  const secondEventDate = firestoreToDateTime(
                    calculation.secondEventTime
                  );
                  return (
                    <TableRow key={calculation.cardId}>
                      <TableCell className="font-medium">
                        {calculation.cardNumber || calculation.cardId.slice(-6)}
                      </TableCell>
                      <TableCell>
                        {calculation.customerName || "Unknown"}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatValue(calculation.timeDifference)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {calculation.technicianName && (
                            <Badge variant="outline" className="text-xs">
                              Tech: {calculation.technicianName}
                            </Badge>
                          )}
                          {calculation.serviceAdvisorName && (
                            <Badge variant="secondary" className="text-xs">
                              SA: {calculation.serviceAdvisorName}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        <div className="space-y-1">
                          <div>
                            Start:{" "}
                            {firstEventDate
                              ? firstEventDate.toLocaleString()
                              : "N/A"}
                          </div>
                          <div>
                            End:{" "}
                            {secondEventDate
                              ? secondEventDate.toLocaleString()
                              : "N/A"}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {filteredAndSortedData.length === 0 && searchTerm && (
            <div className="py-8 text-center text-muted-foreground">
              No records found matching &#34;{searchTerm}&#34;
            </div>
          )}
        </div>
      </CardContent>
    </UICard>
  );
};
